import { DataGridPro, GridSortModel } from '@mui/x-data-grid-pro';
import { GridCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { UserGroup } from '../../../data/models/user-group.model';
import { IconButton, Paper, styled, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  Permission,
  PermissionAction,
  PermissionResource,
  PermissionScope,
} from '../../../data/models/permission.model';
import securityUtils from '../../../services/securityUtils';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'DataTable';

export const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

export const DataTableContainer = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
    minWidth: 0,
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface GroupDataTableProps {
  groups: UserGroup[];
  permissions: Permission[];
  resources: PermissionResource[];
  actions: PermissionAction[];
  scopes: PermissionScope[];
  loading: boolean;
  readOnly: boolean;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onSortModelChange: (model: GridSortModel) => void;
  sortModel: GridSortModel;
}

export default function GroupDataTable({
  groups,
  permissions,
  resources,
  actions,
  scopes,
  loading,
  readOnly,
  onEdit,
  onDelete,
  onSortModelChange,
  sortModel,
}: GroupDataTableProps) {
  const { t } = useTranslation('common');
  const isSuperReaderUser = useIsSuperReaderUser();

  const renderPermissions = (params: GridCellParams) => {
    const perms = params.row.userPermissions?.map((uperms: any) => {
      const pid = uperms.userPermissionID;
      const perm = permissions.find((p) => p.userPermissionID === pid);
      return perm
        ? securityUtils.getPermissionName(perm, resources, actions, scopes)
        : '';
    });
    const s = perms.join(', ');
    return <div title={s}>{s}</div>;
  };

  const columns = [
    {
      field: 'userGroupBC',
      headerName: t('groups.groupName'),
      flex: 1,
    },
    {
      field: 'description',
      headerName: t('groups.description'),
      flex: 1,
    },
    {
      field: 'permissions',
      headerName: t('groups.permissions'),
      flex: 2,
      renderCell: renderPermissions,
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 120,
      hide: readOnly,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {!isSuperReaderUser && (
              <Tooltip title={t('groups.editGroup')}>
                <IconButton
                  data-testid="edit"
                  onClick={() => onEdit(params.row.userGroupID)}
                  size="large">
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('groups.deleteGroup')}>
                <IconButton
                  data-testid="edit"
                  onClick={() => onDelete(params.row.userGroupID)}
                  size="large">
                  <DeleteForeverIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
      sortable: false,
    },
  ];

  return (
    <DataTableContainer data-testid="group-table" className={classes.container}>
      <DataGridPro
        getRowId={(row) => row.userGroupID!}
        rows={groups}
        columns={columns}
        rowCount={groups.length}
        autoHeight={true}
        loading={loading}
        className={classes.grid}
        checkboxSelection={false}
        disableRowSelectionOnClick={true}
        disableColumnMenu={true}
        disableColumnSelector={true}
        hideFooter={true}
        sortingMode="server"
        initialState={{
          sorting: {
            sortModel: [{ field: 'description', sort: 'asc' }],
          },
        }}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingOrder={['asc', 'desc']}
      />
    </DataTableContainer>
  );
}
